import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "How do I know if therapy is right for me?",
    answer: "If you're asking the question, that's often a sign it's worth exploring. You don't need a diagnosis or a crisis to benefit from therapy. Many people come in simply because they feel stuck, overwhelmed, or ready for something to shift.",
  },
  {
    question: "What can I expect during the first session?",
    answer: "The first session is really about getting to know each other. I'll ask about what's bringing you in, a bit of your history, and what you're hoping for. There's no pressure to share more than you're comfortable with — we'll move at your pace.",
  },
  {
    question: "How long does therapy typically last?",
    answer: "It depends on your goals. Some people find what they need in a few months; others benefit from longer-term work. We'll check in regularly so you always feel in control of the process.",
  },
  {
    question: "Do you offer virtual sessions?",
    answer: "Yes. I offer secure, HIPAA-compliant video sessions for anyone located in Texas. Many clients find virtual therapy just as effective — and it removes barriers like commuting and scheduling.",
  },
  {
    question: "What is your cancellation policy?",
    answer: (<>Life happens, and I'm always happy to work with you when I can. I ask for at least 24 hours' notice if you need to cancel or reschedule. Late cancellations and no-shows are subject to the <Link to="/cancellation-policy" className="underline underline-offset-2 text-primary hover:opacity-80 transition-opacity">cancellation policy</Link>.</>),
  },
  {
    question: "Do you accept insurance?",
    answer: "Yes, I'm in-network with Aetna, Blue Cross Blue Shield, Cigna, and United Healthcare for Texas residents. I also offer self-pay rates and a limited number of sliding-scale spots. If you're unsure about coverage, I'm happy to help you check.",
  },
];

const FAQSection = () => {
  return (
    <section id="faq" className="faq-section py-24 px-6 bg-secondary">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-[5fr_9fr] gap-12 lg:gap-16">
          {/* Left — sticky heading + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:sticky md:top-32 md:self-start"
          >
            <h2 className="faq-heading text-3xl md:text-4xl font-heading font-medium text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-16 h-px bg-accent mb-6" />
            <p className="font-body text-muted-foreground font-light leading-relaxed mb-6">
              Have a question not listed here? Don't hesitate to reach out directly.
            </p>
            <a
              href="mailto:ashley@stillwatertherapy.com"
              className="inline-block font-body text-sm font-medium text-primary hover:underline"
            >
              Still have questions? Get in touch →
            </a>
          </motion.div>

          {/* Right — accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Accordion type="single" collapsible className="faq-list space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="faq-item bg-background border border-input rounded-md transition-all hover:border-foreground/30 overflow-hidden"
                >
                  <AccordionTrigger className="faq-question font-heading font-medium text-foreground text-left hover:no-underline py-5 px-6 hover:bg-secondary transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer font-body text-muted-foreground font-light leading-relaxed pt-2 pb-5 px-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
